import {
  createApi,
  QueryDefinition,
  SkipToken,
} from '@reduxjs/toolkit/query/react';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Project } from './models';
import { baseApi, sanitizeForNulls } from '@centerpiece/components';
import { UseQueryStateOptions } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export const project = baseApi
  .enhanceEndpoints({ addTagTypes: ['Projects', 'Project', 'Groupings'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjects: builder.query<Project[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Project' as const, id })),
                'Projects',
                'Groupings',
              ]
            : ['Projects', 'Groupings'],
      }),
      getProject: builder.query<Project, { id: number; params?: object }>({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `project/${id}`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) => [{ type: 'Project', id: arg.id }],
      }),
      createProject: builder.mutation({
        query: request => ({
          url: 'project',
          method: 'POST',
          body: sanitizeForNulls(request),
          // body: request,
        }),
        invalidatesTags: ['Projects'],
        async onQueryStarted(arg, { dispatch, queryFulfilled, requestId }) {
          dispatch(
            createProjectsSlice.actions.update({ requestId, status: 'pending' })
          );

          try {
            await queryFulfilled;
            dispatch(
              createProjectsSlice.actions.update({
                requestId,
                status: 'success',
              })
            );
          } catch (err) {
            dispatch(
              createProjectsSlice.actions.update({
                requestId,
                status: 'rejected',
              })
            );
          }
        },
      }),
      patchProject: builder.mutation<Project, Project>({
        query: (request: Project) => ({
          url: `project/${request.id}`,
          method: 'PATCH',
          body: sanitizeForNulls(request),
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Project', id: arg.id },
        ],
      }),
      updateProject: builder.mutation<Project, Project>({
        query: (request: Project) => ({
          url: `project/${request.id}`,
          method: 'PUT',
          body: sanitizeForNulls(request),
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Project', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project.util.updateQueryData('getProject', { id }, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project.util.invalidateTags(['Project']))
             */
            dispatch(project.util.invalidateTags(['Project']));
          }
        },
      }),
      deleteProject: builder.mutation<Project, number>({
        query: id => ({
          url: `project/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Project' as const, arg }, 'Project'] : ['Project'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchProjectMutation,
  useGetProjectsQuery,
  useGetProjectQuery,
  useLazyGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  usePrefetch: useProjectsPrefetch,
} = project;

export const useGetProjectsQueryState = (
  args: { params?: object } | SkipToken,
  options?: UseQueryStateOptions<
    QueryDefinition<any, any, any, any, string>,
    Record<string, any>
  >
) => project.endpoints.getProjects.useQueryState(args, options);

type CreateProjectStatus = 'pending' | 'success' | 'rejected';

export const createProjectsSlice = createSlice({
  name: 'createProjects',
  initialState: {} as { [key: string]: CreateProjectStatus },
  reducers: {
    init: state => {
      Object.keys(state).forEach(key => delete state[key]);
    },
    update: (
      state,
      action: PayloadAction<{ requestId: string; status: CreateProjectStatus }>
    ) => {
      const { requestId, status } = action.payload;
      state[requestId] = status;
    },
  },
});

const selectCreateProjectsState = (state: any) => state.createProjects;
export const selectCreateProjectsStatus = createSelector(
  selectCreateProjectsState,
  createProjectsState => {
    const statusArray = Object.values(createProjectsState);
    const pending = statusArray.filter(val => val === 'pending').length;
    const success = statusArray.filter(val => val === 'success').length;
    const failed = statusArray.filter(val => val === 'rejected').length;

    return { pending, success, failed };
  }
);

const selectProjectsRoot = project.endpoints.getProjects.select({});
export const selectProjects = createSelector(
  selectProjectsRoot,
  project => project.data || []
);
